import { Link } from "react-router-dom";
import { CheckCircle } from "lucide-react";
import { SEOPageLayout, InlineCTA } from "../../components/seo/SEOPageLayout";

const MEDTRONIC_PRODUCTS = [
  "MMT-242A Advanced Infusion Sets",
  "Medtronic 780G Pump System",
  "MiniMed Pumps",
  "Guardian 4 Transmitter Kits (MMT-784QK)",
  "Guardian Sensors (single boxes and 5-packs)",
  "Quick-Set Infusion Sets (Pack of 10)",
  "Mio Advanced Infusion Sets",
  "Reservoirs (Box of 10)",
];

export default function SellMedtronic() {
  return (
    <SEOPageLayout
      title="Sell Eligible Medtronic Supplies | Cash for Test Strips Jacksonville"
      description="Sell eligible unused Medtronic pump supplies, sensors, and infusion sets in Jacksonville. Learn which Medtronic products we accept and how to get started."
      canonical="https://cashforteststripsjacksonville.com/sell-medtronic-supplies/"
      breadcrumbLabel="Sell Medtronic Supplies"
      primaryKeyword="Sell Medtronic Supplies"
      h1="Sell Eligible Medtronic Supplies"
      intro="If you have unopened Medtronic diabetic pump supplies, sensors, or infusion sets that are still within their shelf life, we may be able to purchase them. This page lists the specific Medtronic items we currently accept and the requirements they must meet."
    >
      {({ openForm }) => (
        <>
          <section data-testid="section-medtronic-accepted">
            <h2 className="text-3xl font-bold text-[#1F2937] mb-4" style={{ fontFamily: "Manrope" }}>
              Medtronic Products We Currently Accept
            </h2>
            <p className="text-lg text-[#4B5563] leading-relaxed mb-6">
              We do not accept every Medtronic product on the market. The list below reflects what we can review today. If your product is not listed, contact us and we will let you know whether we can still help.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              {MEDTRONIC_PRODUCTS.map((p) => (
                <div key={p} className="flex items-start gap-2 bg-[#F5F5F0] rounded-lg p-4">
                  <CheckCircle className="w-5 h-5 text-[#0F5132] mt-0.5 flex-shrink-0" />
                  <span className="text-[#1F2937] font-medium">{p}</span>
                </div>
              ))}
            </div>
          </section>

          <section data-testid="section-medtronic-requirements">
            <h2 className="text-3xl font-bold text-[#1F2937] mb-4" style={{ fontFamily: "Manrope" }}>Medtronic Eligibility Requirements</h2>
            <ul className="space-y-3 text-lg text-[#4B5563]">
              <li className="flex items-start gap-3"><CheckCircle className="w-5 h-5 text-[#0F5132] mt-1 flex-shrink-0" /><span>The retail box must be sealed and unopened.</span></li>
              <li className="flex items-start gap-3"><CheckCircle className="w-5 h-5 text-[#0F5132] mt-1 flex-shrink-0" /><span>At least six months must remain until the printed expiration date.</span></li>
              <li className="flex items-start gap-3"><CheckCircle className="w-5 h-5 text-[#0F5132] mt-1 flex-shrink-0" /><span>Original packaging must be intact — no water damage, tears, or removed labels.</span></li>
              <li className="flex items-start gap-3"><CheckCircle className="w-5 h-5 text-[#0F5132] mt-1 flex-shrink-0" /><span>Not associated with a Medicaid or Medicare program.</span></li>
            </ul>
          </section>

          <section data-testid="section-medtronic-process">
            <h2 className="text-3xl font-bold text-[#1F2937] mb-4" style={{ fontFamily: "Manrope" }}>How to Check Your Medtronic Supplies</h2>
            <p className="text-lg text-[#4B5563] leading-relaxed mb-4">
              Text a photo of the front and back of the box to (904) 955-9646 or fill out our Get Started form. We will review the specific product code, expiration date, and packaging condition and let you know if it qualifies.
            </p>
            <p className="text-lg text-[#4B5563] leading-relaxed">
              You can also review our full <Link to="/accepted-diabetic-supplies/" className="text-[#0F5132] font-semibold underline">accepted diabetic supplies list</Link> or start with the main <Link to="/sell-diabetic-test-strips/" className="text-[#0F5132] font-semibold underline">sell diabetic test strips</Link> page.
            </p>
          </section>

          <InlineCTA
            onGetStarted={openForm}
            headline="Have Medtronic Supplies to Sell?"
            sub="Send us a quick photo — we will confirm whether your product qualifies."
          />
        </>
      )}
    </SEOPageLayout>
  );
}
